import type { CornerRadiusModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { LengthInputSelector } from "@editor/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import AllCorners from "@svg/modifiers/direction/all-corners";
import BottomLeftCorner from "@svg/modifiers/direction/bottom-left-corner";
import BottomRightCorner from "@svg/modifiers/direction/bottom-right-corner";
import TopLeftCorner from "@svg/modifiers/direction/top-left-corner";
import TopRightCorner from "@svg/modifiers/direction/top-right-corner";

import { useSelectedElement } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";

type RadiusMode = "unified" | "individual";

const CORNER_RADIUS_OPTIONS = [
  {
    value: 0,
    label: "None",
    displayValue: "None",
  },
  {
    value: 4,
    label: "Small",
    displayValue: "Small (4px)",
  },
  {
    value: 8,
    label: "Medium",
    displayValue: "Medium (8px)",
  },
  {
    value: 12,
    label: "Large",
    displayValue: "Large (12px)",
  },
  {
    value: 16,
    label: "Extra Large",
    displayValue: "Extra Large (16px)",
  },
  {
    value: 24,
    label: "2X Large",
    displayValue: "2X Large (24px)",
  },
  {
    value: 40,
    label: "3X Large",
    displayValue: "3X Large (40px)",
  },
  {
    value: 72,
    label: "4X Large",
    displayValue: "4X Large (72px)",
  },
] as const;

interface RadiusValue {
  topLeft?: number;
  topRight?: number;
  bottomRight?: number;
  bottomLeft?: number;
}

interface CornerRadiusModifierProps {
  onChange: (modifiers: CornerRadiusModifierValue[]) => void;
  onModeChange?: (isIndividual: boolean) => void;
  showButton?: boolean;
  mode?: "unified" | "individual";
  showIndividualInputs?: boolean;
  onlyShowIndividualInputs?: boolean;
}

const CornerRadiusModifier: React.FC<CornerRadiusModifierProps> = ({
  onChange,
  onModeChange,
  mode: externalMode,
  showIndividualInputs = false,
  onlyShowIndividualInputs = false,
}) => {
  const selectedElement = useSelectedElement();

  const extractedRadius = useMemo(() => {
    return {
      topLeft: selectedElement?.styleOverrides.cornerTopLeftRadius ?? 0,
      topRight: selectedElement?.styleOverrides.cornerTopRightRadius ?? 0,
      bottomRight: selectedElement?.styleOverrides.cornerBottomRightRadius ?? 0,
      bottomLeft: selectedElement?.styleOverrides.cornerBottomLeftRadius ?? 0,
    };
  }, [selectedElement]);

  const [radiusValue, setRadiusValue] =
    useOverridableState<RadiusValue>(extractedRadius);

  const [internalRadiusMode] = useState<RadiusMode>(() => {
    const { topLeft, topRight, bottomRight, bottomLeft } = extractedRadius;
    if (
      topLeft === topRight &&
      topLeft === bottomRight &&
      topLeft === bottomLeft
    ) {
      return "unified";
    }
    return "individual";
  });

  const radiusMode = externalMode ?? internalRadiusMode;

  useEffect(() => {
    onModeChange?.(radiusMode === "individual");
  }, [radiusMode, onModeChange]);

  const handleRadiusChange = useCallback(
    (radius: Partial<RadiusValue>) => {
      const newRadius = { ...radiusValue, ...radius };
      if (radiusMode === "unified") {
        const unifiedValue = newRadius.topLeft;
        newRadius.topRight = unifiedValue;
        newRadius.bottomRight = unifiedValue;
        newRadius.bottomLeft = unifiedValue;
      }
      setRadiusValue(newRadius);

      const modifiers: CornerRadiusModifierValue[] = [];

      if (radiusMode === "unified") {
        const unifiedValue = newRadius.topLeft ?? 0;
        modifiers.push(
          { type: "cornerRadius", corner: "topLeft", radius: unifiedValue },
          { type: "cornerRadius", corner: "topRight", radius: unifiedValue },
          { type: "cornerRadius", corner: "bottomRight", radius: unifiedValue },
          { type: "cornerRadius", corner: "bottomLeft", radius: unifiedValue },
        );
      } else {
        if (radius.topLeft != null) {
          modifiers.push({
            type: "cornerRadius",
            corner: "topLeft",
            radius: radius.topLeft,
          });
        }
        if (radius.topRight != null) {
          modifiers.push({
            type: "cornerRadius",
            corner: "topRight",
            radius: radius.topRight,
          });
        }
        if (radius.bottomRight != null) {
          modifiers.push({
            type: "cornerRadius",
            corner: "bottomRight",
            radius: radius.bottomRight,
          });
        }
        if (radius.bottomLeft != null) {
          modifiers.push({
            type: "cornerRadius",
            corner: "bottomLeft",
            radius: radius.bottomLeft,
          });
        }
      }

      onChange(modifiers);
    },
    [radiusValue, radiusMode, onChange, setRadiusValue],
  );

  const getDisplayValue = (value?: number | null): string => {
    if (showIndividualInputs) {
      return "Mixed";
    }
    if (!value) {
      return "None";
    }
    const matchingOption = CORNER_RADIUS_OPTIONS.find(
      (option) => option.value === value,
    );
    return matchingOption?.displayValue ?? `Custom (${value}px)`;
  };

  const parseRadiusValue = (newValue: string): number => {
    const result = parseUnit(
      newValue,
      { value: 0, unit: "px" },
      "default",
      "px",
    );
    return Number(result.value) ?? 0;
  };

  const radiusOptions = CORNER_RADIUS_OPTIONS.map((option) => ({
    ...option,
    value: option.value.toString(),
    label: (
      <MenuItem
        variant="default"
        size="sm"
        selected={radiusValue.topLeft === option.value}
      >
        {option.label}
      </MenuItem>
    ),
  }));

  const mappedRadiusOptions = CORNER_RADIUS_OPTIONS.map((option) => ({
    ...option,
    value: option.value.toString(),
  }));

  if (onlyShowIndividualInputs) {
    return (
      <div className="grid grid-cols-2 gap-2 items-start">
        {/* Top Left */}
        <LengthInputSelector
          field="topLeftRadius"
          value={radiusValue.topLeft?.toString() ?? "0"}
          onChange={(newValue) =>
            handleRadiusChange({ topLeft: parseRadiusValue(newValue) })
          }
          placeholder="0"
          metrics={["px"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          inputClassName="w-full"
          startEnhancer={<TopLeftCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        {/* Top Right */}
        <LengthInputSelector
          field="topRightRadius"
          value={radiusValue.topRight?.toString() ?? "0"}
          onChange={(newValue) =>
            handleRadiusChange({ topRight: parseRadiusValue(newValue) })
          }
          placeholder="0"
          metrics={["px"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          inputClassName="w-full"
          startEnhancer={<TopRightCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        {/* Bottom Left */}
        <LengthInputSelector
          field="bottomLeftRadius"
          value={radiusValue.bottomLeft?.toString() ?? "0"}
          onChange={(newValue) =>
            handleRadiusChange({ bottomLeft: parseRadiusValue(newValue) })
          }
          placeholder="0"
          metrics={["px"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          inputClassName="w-full"
          startEnhancer={<BottomLeftCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        {/* Bottom Right */}
        <LengthInputSelector
          field="bottomRightRadius"
          value={radiusValue.bottomRight?.toString() ?? "0"}
          onChange={(newValue) =>
            handleRadiusChange({ bottomRight: parseRadiusValue(newValue) })
          }
          placeholder="0"
          metrics={["px"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          inputClassName="w-full"
          startEnhancer={<BottomRightCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
      </div>
    );
  }

  return (
    <div className="corner-radius-modifier">
      <Combobox.Root
        options={radiusOptions}
        value={radiusValue.topLeft?.toString() ?? "0"}
        onChange={(value) => {
          if (showIndividualInputs) {
            onModeChange?.(false);
          }
          handleRadiusChange({ topLeft: parseRadiusValue(value) });
        }}
      >
        <Combobox.Trigger>
          <Combobox.SelectionButton
            layoutClassName="w-full"
            title={getDisplayValue(radiusValue.topLeft)}
            isPlaceholder={!radiusValue}
            size="sm"
            startEnhancer={<AllCorners className="w-3 h-3" />}
            endEnhancer={<ChevronDown size={12} className="text-muted" />}
            titleAlignment="start"
          />
        </Combobox.Trigger>
        <Combobox.Popover
          sideOffset={8}
          side="right"
          align="center"
          layoutClassName="w-[180px]"
        >
          <Combobox.Content
            title="Corner Radius"
            areOptionsSearchable={false}
            emptySearchMessage="No matches found"
          />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default CornerRadiusModifier;
